import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  SearchIcon,
  User,
  XIcon,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useGetAllEmployeesInfiniteQuery } from "@/services/(company)/userApi";
import { startCase } from "lodash";
import { useOnInView } from "react-intersection-observer";
import { useDebounceValue } from "usehooks-ts";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/v2/spinner";
import { ShowWhen } from "@/components/ui/v2/show-when";
import type { FormikProps } from "formik";
import { cn } from "@/utils/cn";
import type { Fields } from "../manage/configuration/configuration-form";

interface IProps {
  formik: FormikProps<Fields>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
}

const evaluatorsFiledName = "evaluators" satisfies keyof Fields;

export const PerformanceEvaluatorsPicker = ({ formik, className }: IProps) => {
  const { t } = useTranslation(["common", "company"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounceValue(searchQuery, 500);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<typeof employees>(
    [],
  );

  const {
    fetchNextPage,
    data: apiResponse,
    isFetchingNextPage,
    isFetching: isFetchingEmployees,
    isLoading: isFirstLoadingEmployees,
  } = useGetAllEmployeesInfiniteQuery({
    limit: 500,
    search: debouncedSearch,
  });
  const employees = apiResponse?.pages.flatMap((page) => page.data) ?? [];

  const selectedEmpIds = formik.values[evaluatorsFiledName] || [];
  const handleToggleInvitee = (id: number) => {
    const currentIds: number[] = (
      formik.values[evaluatorsFiledName] || []
    ).filter(Boolean) as number[];
    const isAlreadySelected = currentIds.includes(id);

    if (isAlreadySelected) {
      formik.setFieldValue(
        evaluatorsFiledName,
        currentIds.filter((item) => item !== id),
      );
      setSelectedEmployees((prev) => prev.filter((emp) => emp.id !== id));
    } else {
      if (selectedEmployees.some((emp) => emp.id === id)) return;

      const emp = employees.find((e) => e.id === id);
      if (emp) setSelectedEmployees((prev) => [...prev, emp]);
      formik.setFieldValue(evaluatorsFiledName, [
        ...new Set([...currentIds, id]),
      ]);
    }
  };

  const handleConfirm = () => {
    formik.setFieldValue(evaluatorsFiledName, selectedEmpIds);
    setIsOpen(false);
  };

  const clearSelection = () => {
    formik.setFieldValue(evaluatorsFiledName, []);
    setSelectedEmployees([]);
  };
  const clearSearchQuery = () => setSearchQuery("");
  const isSelectedEmployees = Boolean(selectedEmpIds.length);

  const hasSelectedInvitees = Boolean(
    formik.values[evaluatorsFiledName] &&
    formik.values[evaluatorsFiledName]?.length > 0,
  );
  const hasError = Boolean(
    formik.errors[evaluatorsFiledName] && formik.touched[evaluatorsFiledName],
  );

  const infiniteScrollAnchor = useOnInView((inView) => {
    if (inView) fetchNextPage();
  });

  useEffect(() => {
    if (employees.length > 0 && selectedEmployees.length === 0) {
      const preSelected = employees.filter((emp) =>
        formik.values[evaluatorsFiledName]?.includes(emp.id),
      );
      if (preSelected.length > 0) setSelectedEmployees(preSelected);
    }
  }, [employees]);

  return (
    <>
      <Card
        className={cn(
          "col-span-2 p-3 rounded-xs",
          hasError && "border-danger",
          className,
        )}
      >
        <FieldGroup className="flex flex-row gap-2">
          {/* ------ FIELD ------ */}
          <Field>
            <FieldLabel>
              {t("company:performance.evaluators_included_label")}
            </FieldLabel>
            <InputGroup className={cn("h-10", hasError && "border-danger")}>
              <InputGroupAddon>
                <Search className="text-black/50" />
              </InputGroupAddon>
              <InputGroupInput
                className="cursor-pointer disabled:opacity-50"
                placeholder={t("common:placeholders.select_employee_name")}
                onClick={() => setIsOpen(true)}
              />
            </InputGroup>
          </Field>
        </FieldGroup>

        {/* Selected employees preview */}
        <ShowWhen when={hasSelectedInvitees}>
          <div className={`flex -space-x-4 items-center py-2`}>
            {formik.values[evaluatorsFiledName] &&
              formik.values[evaluatorsFiledName]?.length > 0 &&
              selectedEmployees.slice(0, 5).map((emp) => (
                <Avatar
                  key={emp.id}
                  data-tooltip-id="hrm-tooltip"
                  data-tooltip-content={emp.employeeName}
                  className="inline-block border-2 border-white "
                >
                  <AvatarImage
                    src={emp?.profileImage ?? undefined}
                    alt={emp.employeeName}
                    className="object-cover object-top"
                  />
                  <AvatarFallback>
                    <User />
                  </AvatarFallback>
                </Avatar>
              ))}
            {formik.values[evaluatorsFiledName] &&
              formik.values[evaluatorsFiledName]?.length > 5 && (
                <Avatar
                  role="button"
                  data-tooltip-id="hrm-tooltip"
                  onClick={() => setIsOpen(true)}
                  data-tooltip-content={t("common:a11y.see_all_invitees")}
                  className="inline-block border-2 border-white"
                >
                  <AvatarFallback>
                    +{formik.values[evaluatorsFiledName]?.length - 5}
                  </AvatarFallback>
                </Avatar>
              )}
            <ShowWhen
              when={Boolean(
                formik.values[evaluatorsFiledName] &&
                formik.values[evaluatorsFiledName]?.length > 0,
              )}
            >
              <small
                role="button"
                className="text-sm text-primary font-semibold ps-6"
                onClick={() => setIsOpen(true)}
              >
                {t("company:performance.people_selected", {
                  count: formik.values[evaluatorsFiledName]?.length ?? 0,
                })}
              </small>
            </ShowWhen>
          </div>
        </ShowWhen>
        <FieldError className="mt-1.5">
          {hasError ? formik.errors[evaluatorsFiledName] : null}
        </FieldError>
      </Card>

      {/* ---- EMPLOYEE PICKER POPUP DIALOG ---- */}
      <Dialog
        open={isOpen}
        onOpenChange={() => {
          setIsOpen((open) => !open);
          clearSearchQuery();
        }}
      >
        <DialogContent showCloseButton={false} aria-describedby={undefined}>
          <DialogHeader className="flex-row items-center justify-between">
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setIsOpen(false)}
                className="h-9 w-9 flex items-center justify-center rounded-sxs border border-border shrink-0"
              >
                <ChevronLeft size={20} className="text-primary rtl:rotate-180" />
              </button>
              <DialogTitle>
                {t("company:performance.select_evaluators_label")}
              </DialogTitle>
            </div>

            {isSelectedEmployees && (
              <button className="text-primary" onClick={clearSelection}>
                {t("company:performance.clear_selection")}
              </button>
            )}
          </DialogHeader>
          {/* Search */}
          <InputGroup>
            <InputGroupInput
              value={searchQuery}
              placeholder={t("common:placeholders.search_employee_name")}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputGroupAddon className="gap">
              <SearchIcon className="text-black/50" />
            </InputGroupAddon>
            <InputGroupAddon align="inline-end">
              {isFetchingEmployees && <Spinner size="sm" />}
              {!isFetchingEmployees && searchQuery.length > 0 && (
                <button onClick={clearSearchQuery} className="px-1">
                  <XIcon size={16} className="text-black/50" />
                </button>
              )}
            </InputGroupAddon>
          </InputGroup>

          {/* Select all & clear selection */}
          <div className="flex justify-between text-sm">
            {employees?.length > 0 && (
              <div className="flex justify-between gap-2  w-full">
                <div className="flex gap-1">
                  <p className="text-gray-500">
                    {t("company:performance.employees_count_label", {
                      count: apiResponse?.pages[0].meta.totalItems ?? 0,
                    })}
                  </p>
                  {isSelectedEmployees && (
                    <p>
                      {t("company:performance.employees_selected", {
                        count: selectedEmpIds.length,
                      })}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => {
                    const allIds = employees
                      .map((emp) => emp.id)
                      .filter(
                        (id): id is number => id !== undefined && id !== null,
                      );
                    formik.setFieldValue(evaluatorsFiledName, allIds);
                    setSelectedEmployees(employees);
                  }}
                >
                  {t("common:buttons.select_all")}
                </button>
              </div>
            )}
          </div>

          <ul className="min-h-80 max-h-80 overflow-y-scroll space-y-2">
            {isFirstLoadingEmployees ? (
              <Spinner />
            ) : employees?.length ? (
              <>
                {employees.map((emp) => (
                  <li
                    key={emp.id}
                    role="button"
                    className="flex items-center justify-between hover:bg-primary/10 rounded-xs p-2 border"
                    onClick={() => handleToggleInvitee(emp.id)}
                  >
                    <div className="flex items-center space-x-4">
                      <Checkbox
                        checked={selectedEmpIds.includes(emp.id)}
                        onCheckedChange={() => handleToggleInvitee(emp.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Avatar>
                        <AvatarImage
                          src={emp?.profileImage ?? undefined}
                          alt={t("common:a11y.profile_picture_alt")}
                          className="object-cover object-top"
                        />
                        <AvatarFallback>
                          <User />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <p>{startCase(emp.employeeName.toLowerCase())}</p>
                        <small className="text-black/50">{emp.position}</small>
                      </div>
                    </div>
                    <ChevronRight className="text-primary" />
                  </li>
                ))}
                {/* Load more employees */}
                <div ref={infiniteScrollAnchor} />
                {isFetchingNextPage && <Spinner wrapperClassName="h-5" />}
              </>
            ) : (
              <small className="block">
                {t("company:performance.no_employees_found")}
              </small>
            )}
          </ul>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                {t("common:buttons.cancel")}
              </Button>
            </DialogClose>
            <Button type="button" onClick={handleConfirm}>
              {t("company:performance.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
